import { Typography } from '@mui/material';
import { AllocationRule } from 'further-types/lp/allocations';
import { FundingSource } from 'further-types/lp/investment';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column } from 'further-ui/components/Layout';
import StaticField from 'further-ui/components/StaticField';
import { AllocationRuleLabels, FundingSourceLabels } from 'further-ui/labels';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { useFormContext } from 'react-hook-form';
import { UpdateCapitalActionFormValues } from '../../../hooks/useCapitalActionForm';
import CapitalCallAllocations from '../CapitalCallAllocations';
import useActionAllocationsChangeHandler from './hooks/useActionAllocationsChangeHandler';

type Props = {
  vehicleId: string;
  entryIndex: number;
  baseCurrency: CurrencyCode;
  investments: Array<{
    id: string;
    assetName: string;
    dealName: string;
    dealCompletionDate: CalendarDay;
    holdingClasses: Array<string>;
    uncalledAmountLcy: number;
    units: number;
    averageUnitPriceLcy: number | null;
    allocationRule?: AllocationRule;
    fundingSource: FundingSource;
  }>;
};

const InvestmentsDetails: React.FC<Props> = ({
  vehicleId,
  investments,
  baseCurrency,
  entryIndex,
}) => {
  const { watch } = useFormContext<UpdateCapitalActionFormValues>();

  const capitalBreakdown = watch(`entries.${entryIndex}.capitalBreakdown`);

  const handleAllocationsChange = useActionAllocationsChangeHandler(entryIndex);

  return (
    <Column spacing="lg">
      {investments.map((investment, index) => (
        <Column key={investment.id} spacing="sm">
          <Typography variant="h4">
            Investment {index + 1}: {investment.dealName} (
            {investment.dealCompletionDate.toLabel()})
          </Typography>
          <Column spacing="sm">
            <StaticField label="Asset" spacing="xxs">
              {investment.assetName}
            </StaticField>
            <StaticField label="Deal name" spacing="xxs">
              {investment.dealName}
            </StaticField>
            <StaticField label="Deal completion date" spacing="xxs">
              {investment.dealCompletionDate.toLabel()}
            </StaticField>
            <StaticField label="Holding class(es)" spacing="xxs">
              {investment.holdingClasses.join(', ')}
            </StaticField>
            <StaticField label="Total investment value" spacing="xxs">
              {numberToCurrencyString(investment.uncalledAmountLcy, {
                currency: baseCurrency,
              })}
            </StaticField>
            <StaticField label="Units" spacing="xxs">
              {investment.units}
            </StaticField>
            <StaticField label="Average unit price" spacing="xxs">
              {numberToCurrencyString(investment.averageUnitPriceLcy, {
                currency: baseCurrency,
              })}
            </StaticField>
            <StaticField label="Allocation rule" spacing="xxs">
              {investment.allocationRule
                ? AllocationRuleLabels[investment.allocationRule]
                : 'N/A'}
            </StaticField>
            <StaticField label="Funding source" spacing="xxs">
              {FundingSourceLabels[investment.fundingSource]}
            </StaticField>
          </Column>
          <CapitalCallAllocations
            entryIndex={entryIndex}
            title="Investment allocation"
            calledFundsLabel="Investment capital call"
            currency={baseCurrency}
            actionId={investment.id}
            allocationRule={AllocationRule.LinkedAction}
            capitalCalledLcy={investment.uncalledAmountLcy}
            vehicleId={vehicleId}
            capitalBreakdown={capitalBreakdown ?? []}
            keepCallAmountFixed={true}
            onChange={(capitalBreakdown) => {
              handleAllocationsChange(investment.id, capitalBreakdown);
            }}
          />
        </Column>
      ))}
    </Column>
  );
};

export default InvestmentsDetails;
